import { getAllReusableTransformAiFunction } from 'js/actions';
import { HeaderField } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import {
  baseSelectStyles,
  SelectInput,
  SelectOption,
  SelectValue,
} from 'js/components/elements/rselect/ReactSelectUtils';
import SortedSelect from 'js/components/elements/rselect/SortedSelect';
import { GET_CODE_CONTAINERS_AI_FUNCTIONS_REQUEST } from 'js/constants/actionTypes';
import PopUpForm from 'js/components/elements/pop-up-form/PopUpForm';
import { KeyValueEditor } from 'new/elements/KeyValueEditor';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { clearKwargsFromExtraData, useAIFunctionKwargs } from './useAIFunctionKwargs';
import useAppDispatch from 'js/hooks/useAppDispatch';

interface DropdownWithSectionsProps {
  field: any;
  sourceConfig: any;
  onSourceConfigChanged: (newSourceConfig: any) => void;
  onFieldUpdated: (field: string, value: any) => void;
}

const DropdownWithSections: React.FC<DropdownWithSectionsProps> = ({
  field,
  sourceConfig,
  onSourceConfigChanged,
  onFieldUpdated,
}) => {
  const SOURCE_CONFIG_FIELD_VALUE = field.name;
  const chunkerId = sourceConfig?.[SOURCE_CONFIG_FIELD_VALUE];
  const dispatch = useAppDispatch();
  const chunkers = useSelector((state: any) => state.transforms.chunkers);
  const isLoading = useIsLoading(GET_CODE_CONTAINERS_AI_FUNCTIONS_REQUEST);

  const [selectedChunker, setSelectedChunker] = useState<any>(null);
  const isRendered = useRef(false);

  const chunkersArray = useMemo(() => {
    if (!chunkers) return [];
    return Array.isArray(chunkers) ? [...chunkers] : Array.from(chunkers as Iterable<any>);
  }, [chunkers]);

  const chunkersOptions = useMemo(() => {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    return chunkersArray
      .map((chunker) => ({ value: chunker.id, label: chunker.name }))
      .sort((a, b) => collator.compare(a.label ?? '', b.label ?? ''));
  }, [chunkersArray]);

  const { rows, duplicateRowIds, missingKeyRowIds, invalidNumberRowIds, handleKwargsChange } =
    useAIFunctionKwargs({
      sourceConfig,
      onSourceConfigChanged,
    });

  // currently, we are not able to uncheck a checkbox from our mock json file when
  // another checkbox is checked. This is a workaround to ensure that the checkbox is
  // unchecked when the component is rendered.
  useEffect(() => {
    const useCustomProcessor = sourceConfig['ui.use_custom_processor'];
    const newSourceConfig = { ...sourceConfig };

    if (!isRendered.current && useCustomProcessor) {
      newSourceConfig['ui.use_custom_processor'] = false;
      onSourceConfigChanged(newSourceConfig);
      return;
    }

    if (isRendered.current && useCustomProcessor) {
      newSourceConfig['ai.function'] = false;
      newSourceConfig['ai.function.id'] = null;
      onSourceConfigChanged(newSourceConfig);
    }

    isRendered.current = true;
  }, [sourceConfig, onSourceConfigChanged]);

  useEffect(() => {
    if (chunkerId && chunkers) {
      const chunker = chunkers.find((chunker) => chunker.id === chunkerId);
      if (chunker && (!selectedChunker || selectedChunker.id !== chunker.id)) {
        setSelectedChunker(chunker);
      }
    }
  }, [sourceConfig, chunkers, chunkerId]);

  useEffect(() => {
    if (!chunkersArray.length) {
      dispatch(getAllReusableTransformAiFunction());
    }
  }, [dispatch, chunkersArray.length]);

  // One-shot auto-select: when the AI Function is enabled but no id is set yet,
  // pick the first chunker as soon as the chunkers list finishes loading. This
  // runs at most once per mount — the ref guard prevents it from firing again
  // on subsequent renders (e.g. user picking a different chunker), which was
  // the cause of the "first click swallowed" regression vs. develop.
  const hasAutoSelectedRef = useRef(false);

  useEffect(() => {
    if (hasAutoSelectedRef.current) return;
    if (!chunkersOptions.length) return;
    hasAutoSelectedRef.current = true;

    const aiEnabled = sourceConfig?.['ai.function'];
    const aiIdSet = sourceConfig?.[SOURCE_CONFIG_FIELD_VALUE];
    if (aiEnabled && !aiIdSet) {
      const firstId = chunkersOptions[0].value;
      const chunker = chunkersArray.find((c) => c.id === firstId);
      if (chunker) {
        onSourceConfigChanged({ ...sourceConfig, [SOURCE_CONFIG_FIELD_VALUE]: firstId });
        setSelectedChunker(chunker);
      }
    }
  }, [chunkersOptions]);

  const handleChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : null;
    const newSourceConfig = { ...sourceConfig };

    if (value === null) {
      if (selectedChunker?.custom_config) {
        selectedChunker?.custom_config.length > 0 &&
          selectedChunker?.custom_config?.forEach(({ name }) => {
            delete newSourceConfig[`chunker_params.${name}`];
          });
      }

      newSourceConfig[SOURCE_CONFIG_FIELD_VALUE] = null;
      newSourceConfig['ai.function.name'] = null;
      clearKwargsFromExtraData(newSourceConfig);

      onSourceConfigChanged(newSourceConfig);
      setSelectedChunker(null);

      return;
    }

    // Note: stale kwargs from the previous chunker are stripped reactively in
    // the chunker-id-change useEffect inside useAIFunctionKwargs, not here.
    // Mutating extra_data synchronously in this handler was causing react-select
    // to swallow the first click when the source had saved kwargs.
    const chunker = chunkersArray.find((chunker) => chunker.id === value);

    if (
      Object.keys(chunker?.custom_config)?.length === 0 &&
      selectedChunker?.custom_config?.props?.length > 0
    ) {
      selectedChunker.custom_config.props.forEach(({ name }) => {
        delete newSourceConfig[`chunker_params.${name}`];
      });
    }

    newSourceConfig[SOURCE_CONFIG_FIELD_VALUE] = value;

    onSourceConfigChanged(newSourceConfig);
    setSelectedChunker(chunker);
  };

  const onFieldChanged = (field, value) => {
    onFieldUpdated(field, value);
  };

  // Drive the dropdown's displayed value from local `selectedChunker` state.
  // `setSelectedChunker` runs synchronously in `handleChange` when the user
  // picks an option, so the dropdown updates on the first click without
  // waiting for the sourceConfig prop round-trip through Redux.
  // `selectedChunker` is kept in sync with sourceConfig externally via the
  // syncing useEffect below, so external changes (e.g. effect 1 clearing
  // ai.function.id) still update the dropdown correctly.
  const selectedValueFromArray = useMemo(() => {
    if (!selectedChunker) return null;
    return chunkersOptions.find((opt) => opt.value === selectedChunker.id) ?? null;
  }, [selectedChunker, chunkersOptions]);

  const customConfigFieldsFromSelectedChunker = selectedChunker?.custom_config?.props as any[];

  const formFields =
    customConfigFieldsFromSelectedChunker && customConfigFieldsFromSelectedChunker.length > 0
      ? customConfigFieldsFromSelectedChunker.map(
          ({ name, label, description, placeholder, type }) => {
            return {
              name: `chunker_params.${name}`,
              display_name: label || name,
              description: description || placeholder || '',
              inputType: type || 'text',
              required: false,
              value: sourceConfig[`chunker_params.${name}`] || '',
            };
          },
        )
      : null;

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ width: '100%', flexShrink: 0 }}>
        <SortedSelect
          variant="default"
          label="Select a chunker:"
          defaultValue={selectedValueFromArray}
          value={selectedValueFromArray}
          isClearable={!!selectedValueFromArray}
          isSearchable
          isLoading={isLoading}
          backspaceRemovesValue
          onChange={handleChange}
          styles={baseSelectStyles}
          components={{
            Input: SelectInput,
            Option: SelectOption,
            SingleValue: SelectValue,
          }}
          options={[...chunkersOptions]}
        />

        {formFields && (
          <PopUpForm
            rootClassName="nodrag nowheel"
            data={sourceConfig || {}}
            fields={formFields}
            onFieldUpdated={(field, value) => onFieldChanged(field, value)}
            showCloseButton={false}
            showRequiredIndicator={false}
            fieldClassNames={['advanced-configuration']}
            rootStyle={{
              backgroundColor: 'var(--color-greyscale-0)',
              padding: '16px 0 0 0',
            }}
          />
        )}

        {selectedValueFromArray && (
          <div style={{ marginTop: 'var(--spacing-lg)' }}>
            <HeaderField
              item={{
                display_name: 'AI Function Inputs (optional)',
                description:
                  'Optional inputs to pass to the AI Function. Add a key and value for each parameter the function accepts - for example, a prompt, a model name, or a schema ID.',
              }}
              className="field-label-name"
            />

            <KeyValueEditor
              rows={rows}
              onChange={handleKwargsChange}
              duplicateRowIds={duplicateRowIds}
              missingKeyRowIds={missingKeyRowIds}
              invalidNumberRowIds={invalidNumberRowIds}
              addLabel="Add"
              keyPlaceholder="Key"
              valuePlaceholder="Value"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DropdownWithSections;
